import { escalationRequestFormTypes } from "@twiliointernal/services-common/containers/EscalationRequestForm";
import { supportPlansViewTypes } from "@twiliointernal/services-common/containers/SupportPlansView";
import { ticketDetailsViewTypes } from "@twiliointernal/services-common/containers/TicketDetailsView";
import { ticketHistoryViewTypes } from "@twiliointernal/services-common/containers/TicketHistoryView";
import { ticketSubmissionViewTypes } from "@twiliointernal/services-common/containers/TicketSubmissionView";

import { RelevanceValue, SearchResultResponse, Suggestion } from "./search";
import { UserDetails } from "./user";
import { ZendeskTags } from "./zendesk";

export interface SupportApiResponse<Type> {
  data: Type;
  errorCode?: string;
  message?: string;
  statusCode: number;
  status: number;
  success: boolean;
}

export type Filter = {
  status?: string;
  statusIs?: string;
  priority?: string;
  createdBefore?: string;
  createdAfter?: string | Date | undefined;
  product?: string;
};

export interface CreateTicketData {
  id: number;
}

export interface CollaboratorsFormValues {
  collaborators: string;
}

export interface UpdateTicketArgs {
  ticketId: string;
  data:
    | ticketDetailsViewTypes.TicketCommentSubmissionArgs
    | CollaboratorsFormValues;
}

export interface EscalateTicketProps {
  ticketId: number;
  escalationType: escalationRequestFormTypes.EscalationType;
  additionalInfo: string;
}

// ⬇️ Types for support-api route responses
// Source of truth: https://wiki.hq.twilio.com/pages/viewpage.action?spaceKey=COM&title=Account+Service+API+v3#AccountServiceAPIv3-AccountStatusCodes
export enum AccountStatus {
  unpersisted = "unpersisted",
  initialized = "initialized",
  active = "active",
  suspended = "suspended",
  deleted = "deleted",
  parentSuspended = "parent_suspended",
  suspendedByParent = "suspended_by_Parent",
}

export interface AccountDetails {
  active: number;
  currentAccountSid: string;
  dateCreated: string;
  dateUpdated: string;
  emailAddr: string;
  emailValidated: boolean;
  firstName: string;
  flags: number[];
  friendlyName: string;
  isoCountryCode: string;
  lastName: string;
  parentAccountSid?: string;
  preferredLanguage: string;
  sid: string;
  status: AccountStatus;
  upgraded: boolean;
  userType: number;
  version: number;
}

export interface Article {
  id: number;
  title: string;
  body: string;
  attachments: ArticleAttachment[];
}

export interface CategoriesResponse {
  categories: Category[];
}

export interface ArticleAttachment {
  fileName: string;
  contentUrl: string;
  contentType: string;
  url: string;
}

export interface ArticleByIdResponse {
  article: Article;
}

export interface Category {
  id: number;
  name: string;
  sections: Section[];
  articles: Article[];
}

export interface ChildArticleData {
  entities: Article[];
  count: number;
}

export interface CategoryByIdResponse {
  category: Category;
}

export type GetTicketQueryParams = {
  filter?: Filter;
  sort?: {
    sortBy?: string;
    sortOrder?: string;
  };
  paginate?: {
    perPage?: number;
    page?: number;
  };
  scope: ticketHistoryViewTypes.TicketHistoryScopeType;
};

export interface GetSupportPlanResponse {
  currentPlan: supportPlansViewTypes.SupportPlanResponse;
  pendingPlan?: supportPlansViewTypes.SupportPlanResponse;
}

// Unauthenticated user's will have 'data: undefined'
export type GetUser = GetUserAuthenticated | undefined;

export interface GetUserAuthenticated {
  account: AccountDetails;
  user: UserDetails;
}

export interface FeatureToggleResponse {
  errorCode?: string;
  message?: string;
  features: FeatureToggles;
  statusCode: number;
  success: boolean;
}

export interface FeatureToggles {
  [key: string]: {
    updated: string;
    isOn: boolean;
  };
}

export interface SectionByIdResponse {
  section: Section;
}

export interface Section {
  id: number;
  name: string;
  category: Category;
  articles?: Article[];
}

export interface TicketFieldDetails {
  id: number;
  name: string;
  raw_name: string;
  value: string;
  default: boolean;
}

export interface TicketFieldByIdData {
  custom_field_options: TicketFieldDetails[];
}

export interface Translation {
  id: number;
  url: string;
  htmlUrl: string;
  sourceId: number;
  sourceType: string;
  locale: string;
  title: string;
  body: string;
  outdated: boolean;
  draft: boolean;
  hidden: boolean;
  createdAt: string;
  updatedAt: string;
  updatedById: number;
  createdById: number | null;
}

export interface SearchResultsResponseData {
  queryId: string;
  results: SearchResultResponse[];
  totalNumberOfResults: number;
}

export interface SearchSuggestionResults {
  querySuggestionsId: string;
  suggestions: Suggestion[];
}

export interface SendUserFeedbackResponse {
  data: boolean;
  statusCode: number;
  success: boolean;
}

export interface SystemStatusResponse {
  name: string;
  status: string;
}

export interface UserFeedbackArgs {
  queryId: string;
  resultId: string;
  relevanceValue?: RelevanceValue;
  clickTime?: number;
}

export interface TicketHistoryResponse {
  count: number;
  results: ticketHistoryViewTypes.Ticket[];
  metadata: ticketHistoryViewTypes.TicketHistoryPermissions;
}

export interface UpdateSupportPlanArgs {
  billableItemSid: string;
  startDate: string;
}

export interface UserAccountsResponse {
  accounts: AccountDetails[];
  count: number;
}

export interface TicketSubmissionData
  extends ticketSubmissionViewTypes.OneSupportCenterFormValues {
  tags: ZendeskTags[];
  chatId?: string;
}
